import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { PageHeader } from "CommonComponent";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  exportEngineConfiguration,
  fetchEngineDashboard,
  getErrorMessage,
  importEngineConfiguration,
} from "../pafEngineApi";
import "../PafEngine.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const formatDt = (value) =>
  value ? String(value).replace("T", " ").slice(0, 16) : "—";

const PafEngineDashboard = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [data, setData] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    dispatch(showCommonLoader());
    try {
      const payload = await fetchEngineDashboard(objectViewedId);
      setData(payload);
    } catch (error) {
      setData(null);
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  }, [dispatch, objectViewedId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    dispatch(showCommonLoader());
    try {
      const config = await exportEngineConfiguration(objectViewedId);
      const blob = new Blob([JSON.stringify(config, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paf-engine-config-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      dispatch(notify("Configuration exported.", "success"));
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    dispatch(showCommonLoader());
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const result = await importEngineConfiguration(payload, objectViewedId);
      dispatch(
        notify(
          `Imported ${result.imported_policies || 0} policies` +
            (result.updated_global ? " and global settings." : "."),
          "success"
        )
      );
      if (result.warnings?.length) {
        dispatch(notify(result.warnings[0]));
      }
      await load();
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  };

  const g = data?.global_settings || {};
  const p = data?.policies || {};
  const s = data?.scheduler || {};
  const last = data?.last_run;
  const health = data?.health || { checks: [], healthy: true };

  return (
    <div className="paf-settings-page">
      <PageHeader title="PAF Settings">
        <div className="paf-engine-actions">
          <Button variant="outline-secondary" size="sm" onClick={load}>
            Refresh
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={handleExport}>
            Export Configuration
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => fileRef.current?.click()}
          >
            Import Configuration
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            hidden
            onChange={handleImportFile}
          />
        </div>
      </PageHeader>

      <div className="main-content-div page-card">
        <div
          className={`paf-engine-status-banner ${
            health.healthy ? "is-healthy" : "is-unhealthy"
          }`}
        >
          <div className="paf-engine-section-title" style={{ marginTop: 0 }}>
            Engine Status {health.healthy ? "— Healthy" : "— Needs Attention"}
          </div>
          <ul className="paf-engine-status-list">
            {(health.checks || []).map((check) => (
              <li key={check.code} className={`level-${check.level}`}>
                {check.level === "ok" ? "✓" : check.level === "warning" ? "⚠" : "✖"}{" "}
                {check.message}
              </li>
            ))}
          </ul>
        </div>

        <Row className="g-3 mb-3">
          <Col md={3}>
            <div className="paf-engine-card">
              <h6>Auto Generate</h6>
              <div className="metric">{g.auto_generate_enabled ? "On" : "Off"}</div>
              <div className="meta">
                Probation: {g.probation_period || "—"} {g.probation_unit || ""}
              </div>
            </div>
          </Col>
          <Col md={3}>
            <div className="paf-engine-card">
              <h6>Policies</h6>
              <div className="metric">{p.total ?? 0}</div>
              <div className="meta">Auto-generate: {p.auto_generate ?? 0}</div>
            </div>
          </Col>
          <Col md={3}>
            <div className="paf-engine-card">
              <h6>PAFs Today</h6>
              <div className="metric">{data?.pafs_generated_today ?? 0}</div>
              <div className="meta">Errors today: {data?.generation_errors_today ?? 0}</div>
            </div>
          </Col>
          <Col md={3}>
            <div className="paf-engine-card">
              <h6>Scheduler</h6>
              <div className="metric" style={{ fontSize: 18 }}>
                {s.schedule || "daily 00:05"}
              </div>
              <div className="meta">Next: {formatDt(s.next_run)}</div>
              <div className="meta">Avg: {s.average_duration_ms ?? 0} ms</div>
              <div className="meta">
                Failures streak: {s.consecutive_failures ?? 0}
              </div>
              <div className="meta">Last failure: {formatDt(s.last_failure)}</div>
            </div>
          </Col>
        </Row>

        <div className="paf-engine-section-title">Last Generation Run</div>
        <div className="paf-engine-result-box mb-3">
          {last ? (
            <div>
              <div>
                <strong>{last.trigger}</strong> · {last.dry_run ? "Dry run" : "Live"} ·{" "}
                {last.status}
              </div>
              <div className="meta">
                Scanned {last.employees_scanned} · Eligible {last.eligible} · Generated{" "}
                {last.generated} · Skipped {last.skipped} · {last.duration_ms} ms
              </div>
            </div>
          ) : (
            <div className="meta">No runs recorded yet.</div>
          )}
        </div>

        <div className="paf-engine-section-title">Quick Actions</div>
        <div className="paf-engine-actions">
          <Link className="btn btn-primary btn-sm" to="/paf-settings/simulation">
            PAF Preview
          </Link>
          <Link className="btn btn-outline-primary btn-sm" to="/paf-settings/generate">
            Manual Generation
          </Link>
          <Link className="btn btn-outline-secondary btn-sm" to="/paf-settings/logs">
            View Logs
          </Link>
          <Link className="btn btn-outline-secondary btn-sm" to="/paf-settings/global-settings">
            Global Settings
          </Link>
          <Link className="btn btn-outline-secondary btn-sm" to="/paf-settings/policies">
            Generation Policies
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PafEngineDashboard;
